import DocumentShell, { DocSection } from '../../shared/documents/DocumentShell';
import PaymentSummary from '../../shared/documents/PaymentSummary';
import Icon from '../../shared/components/Icons';
import { fmtDay } from '../../shared/data/common';
import { cancellationLines } from './data';
import { durationLabel, timeLabel } from './searchModel';
import { useLiveBus } from './BusTrip';

const name = (t) => [t.title, t.firstName, t.lastName].filter(Boolean).join(' ');
const at = (iso) => (iso ? `${fmtDay(iso.slice(0, 10))}, ${timeLabel(iso)}` : '');

/** Printable bus e-ticket: operator PNR, journey, boarding and dropping points, passengers and seats. */
export default function BusTicket({ booking }) {
  const s = booking.summary;
  const { details } = useLiveBus(booking);
  const pnr = details?.operatorPnr || booking.operatorPnr;
  const status = details?.status;
  const passengers = details?.passengers?.length
    ? details.passengers.map((p) => ({ name: p.name, seat: p.seatId, age: p.age, gender: p.gender }))
    : booking.travellers.map((t, i) => ({ name: name(t), seat: s.seats[i], age: t.age, gender: t.gender }));
  const notice = booking.status === 'cancelled' || status === 'Cancelled'
    ? 'This ticket has been cancelled and is not valid for travel.'
    : status === 'ServiceCancelled'
      ? 'The operator has cancelled this trip. This ticket is not valid for travel; the full amount will be refunded.'
      : null;

  return (
    <DocumentShell
      title="Bus E-Ticket"
      subtitle={`${s.from} → ${s.to} · ${fmtDay(s.date)}`}
      booking={booking}
      backTo={`/bus/bookings/${booking.id}`}
      references={[
        ['Operator PNR', pnr || 'Shared by the operator', true],
        ['Ticket no.', booking.supplierBookingId || booking.ref],
        ['Booking ID', booking.id],
      ]}
      barcode={pnr || booking.supplierBookingId || booking.id}
      notice={notice}
    >
      <DocSection title="Journey">
        <div className="doc-segment">
          <div className="doc-seg-airline">
            <Icon name="bus" size={30} />
            <div>
              <b>{details?.operator || s.operator}</b>
              <span>{details?.busType || s.busType}</span>
              {s.mTicketAllowed && <span>m-Ticket accepted</span>}
            </div>
          </div>
          <div className="doc-seg-point">
            <span className="doc-seg-label">Boarding</span>
            <b className="doc-time">{timeLabel(s.boarding.time || s.departure)}</b>
            <span>{fmtDay((s.boarding.time || s.departure).slice(0, 10))}</span>
            <b>{s.boarding.name}</b>
            <span className="doc-small">{[s.boarding.landmark, s.boarding.address].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') || s.from}</span>
          </div>
          <div className="doc-seg-path">
            <span>{durationLabel(s.duration)}</span>
            <span className="doc-seg-line" />
            <span>Departs {timeLabel(s.departure)}</span>
          </div>
          <div className="doc-seg-point">
            <span className="doc-seg-label">Dropping</span>
            <b className="doc-time">{s.dropping?.time ? timeLabel(s.dropping.time) : timeLabel(s.arrival)}</b>
            <span>{fmtDay((s.dropping?.time || s.arrival || s.date).slice(0, 10))}</span>
            <b>{s.dropping?.name || s.to}</b>
            <span className="doc-small">{s.dropping?.address || s.to}</span>
          </div>
        </div>
        <p className="doc-small">Reach the boarding point at least 15 minutes before {at(s.boarding.time) || 'departure'}. Boarding times are set by the operator and may change; the operator will call you if they do.</p>
      </DocSection>

      <DocSection title="Passengers">
        <table className="doc-table">
          <thead><tr><th>#</th><th>Passenger</th><th>Seat</th><th>Age</th><th>Gender</th></tr></thead>
          <tbody>
            {passengers.map((p, i) => (
              <tr key={`${p.seat}-${i}`}><td>{i + 1}</td><td><b>{p.name}</b>{i === 0 && <span className="doc-small"> · Primary</span>}</td><td><b>{p.seat}</b></td><td>{p.age || '—'}</td><td>{p.gender || '—'}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="doc-small">Contact: {booking.contact.phone} · {booking.contact.email}{details?.operatorContact ? ` · Operator: ${details.operatorContact}` : ''}</p>
      </DocSection>

      <div className="doc-columns">
        <DocSection title="Cancellation policy">
          {s.cancellation?.length ? (
            <ul className="doc-list">{cancellationLines(s.cancellation).map((l) => <li key={l}>{l}</li>)}</ul>
          ) : <p className="doc-small">As per the operator's policy.</p>}
          <p className="doc-small">
            {s.partialCancellation ? 'Seats can be cancelled individually. ' : 'All seats on this ticket are cancelled together. '}
            No refund if you miss the bus. The convenience fee is not refundable.
          </p>
        </DocSection>
        <PaymentSummary booking={booking} />
      </div>

      <DocSection title="Important information" className="doc-info">
        <ul>
          <li>Show this e-ticket (printed or on your phone) and a valid government photo ID for every passenger when boarding.</li>
          {s.idProofRequired && <li>The operator checks the ID proof given at booking for the primary passenger — carry the same ID.</li>}
          <li>Ladies seats are reserved for women; the operator may change seats to keep this arrangement.</li>
          <li>The operator is responsible for the journey, timings, amenities and any delays or changes en route.</li>
          <li>Luggage is carried at the passenger's risk. Operators may charge for excess or bulky luggage.</li>
          <li>To cancel, use My Trips or call support before departure. Cancellations follow the policy above.</li>
        </ul>
      </DocSection>
    </DocumentShell>
  );
}
